import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";
import { AdminHeader, errMessage } from "./shared";

type SyncRun = {
  kind: string;
  status: string;
  message: string | null;
  runAt: string;
};

type SyncStatus = {
  keyConfigured: boolean;
  runs: SyncRun[];
};

const KIND_LABEL: Record<string, string> = {
  tournaments: "Torneios",
  field: "Field (participantes)",
  leaderboard: "Leaderboard",
  players: "Golfistas",
  owgr: "Ranking mundial (OWGR)",
};

export default function AdminSincronizacaoPage() {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<SyncStatus>("/api/sync/status");
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(errMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Sincronização"
        caption="SportsDataIO — última execução por tipo"
        action={
          <Button
            title="Atualizar"
            icon={<RefreshCw size={14} />}
            variant="secondary"
            loading={refreshing}
            onClick={() => void refresh()}
            className="h-10 px-3 text-sm"
          />
        }
      />

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!status && !error && <ScreenSpinner />}

      {status && (
        <Card className="flex items-center justify-between p-4">
          <p className="text-sm font-semibold">Chave da API</p>
          <Chip tone={status.keyConfigured ? "success" : "destructive"}>
            {status.keyConfigured ? "Configurada" : "Não configurada"}
          </Chip>
        </Card>
      )}

      <div className="flex flex-col gap-2 pb-6">
        {status?.runs.map((run) => (
          <Card key={run.kind} className="flex items-center gap-3 p-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">
                {KIND_LABEL[run.kind] ?? run.kind}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatDateTime(run.runAt)}
                {run.message && ` · ${run.message}`}
              </p>
            </div>
            <Chip tone={run.status === "ok" ? "success" : "destructive"}>
              {run.status === "ok" ? "OK" : run.status.toUpperCase()}
            </Chip>
          </Card>
        ))}
        {status && status.runs.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            Nenhuma execução registrada ainda.
          </Card>
        )}
      </div>
    </div>
  );
}
